import { Lock, RefreshCw, ServerCrash, SearchX, WifiOff } from 'lucide-react';

import { normalizeError } from '@/shared/api/errors';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';

export interface ErrorStateProps {
  error: unknown;
  /** Omit to hide the retry affordance (e.g. for a 403, retrying is pointless). */
  onRetry?: () => void;
  className?: string;
}

const ICONS = {
  network: WifiOff,
  timeout: WifiOff,
  forbidden: Lock,
  unauthorized: Lock,
  not_found: SearchX,
} as const;

/**
 * Renders a failed query in a way the user can act on: what went wrong, and
 * whether trying again could help. Retry is hidden for errors that are not
 * transient, so we never invite a user to bang on a locked door.
 */
export function ErrorState({ error, onRetry, className }: ErrorStateProps) {
  const apiError = normalizeError(error);
  const Icon = ICONS[apiError.kind as keyof typeof ICONS] ?? ServerCrash;
  const canRetry =
    Boolean(onRetry) && apiError.kind !== 'forbidden' && apiError.kind !== 'not_found';

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      <span className="bg-destructive-subtle text-destructive flex size-16 items-center justify-center rounded-full">
        <Icon className="size-7" aria-hidden />
      </span>
      <div className="space-y-1">
        <p className="font-semibold">مشکلی پیش آمد</p>
        <p className="text-muted-foreground mx-auto max-w-xs text-sm leading-relaxed text-balance">
          {apiError.message}
        </p>
      </div>
      {canRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
          <RefreshCw className="size-4" />
          تلاش دوباره
        </Button>
      ) : null}
    </div>
  );
}
